import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Drawer, Avatar, Typography, Input, Select, DatePicker, message, Dropdown } from "antd";
import {
  CalendarOutlined,
  UserOutlined,
  ExpandOutlined,
  ShareAltOutlined,
  CheckCircleOutlined,
  TagOutlined,
  FileTextOutlined,
  CheckSquareOutlined,
  CloseCircleOutlined,
  WechatWorkOutlined,
  IssuesCloseOutlined,
  CheckOutlined,
  CloseOutlined,
  LinkOutlined,
  MailOutlined,
} from "@ant-design/icons";
import CommentsSection from "./CommentSection";
import AttachmentsSection from "./AttachmentsSection";
import useTasks from "hooks/projects/useTasks";
import { useFileUpload } from "hooks/useFileUpload";
import dayjs from "dayjs";
import { useAuth } from "contexts/AuthContext";
import { useTags } from "hooks";
import { SimpleTagManager } from "./SimpleTagManager";
import useFormattedDate from "hooks/useFormatDate";
import useProcessedDescription from "hooks/useProcessedDescription";
import {
  convertArrayRefsToFileService,
  convertFileServiceImagesToArrayRefs,
} from "utils/taskUtils";
import {
  formatNameWithEmail,
  getTaskAssigneeInfo,
  getUserDisplayName,
} from "utils/taskAssignee";
import MentionEditor from "../../common/MentionEditor";
import apiClient from "utils/apiClient";
import paths from "routes/paths";
import useWorkspaceMembers, {
  getWorkspaceMemberDisplayName,
  getWorkspaceMemberEmail,
} from "hooks/workspaces/useWorkspaceMembers";

const { Title, Text } = Typography;

const statusMap = {
  OPEN: { label: "Open", color: "#9ca3af" }, // gray
  IN_PROGRESS: { label: "In Progress", color: "#3b82f6" }, // blue
  COMPLETED: { label: "Completed", color: "#22c55e" }, // green
  BLOCKED: { label: "Blocked", color: "#ef4444" }, // red
};

const taskStatuses = [
  { value: "OPEN", label: "Open" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "COMPLETED", label: "Completed" },
  { value: "BLOCKED", label: "Blocked" },
  { value: "BACKLOG", label: "Backlog" },
  { value: "CANCELLED", label: "Cancelled/Abandoned" },
];

const assignmentStatuses = [
  { value: "ACCEPTED", label: "Accepted" },
  { value: "OTW", label: "On The Way" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "EXPIRED", label: "Expired" },
];

const priorities = [
  { value: 0, label: "Critcal" },
  { value: 1, label: "High" },
  { value: 2, label: "Medium" },
  { value: 3, label: "Low" },
];

const storyPoints = [1, 2, 3, 5, 8, 13].map((p) => ({
  value: p,
  label: p,
}));

const normalizeUrl = (url = "") => (url ? url.split("?")[0] : "");

const TaskDrawer = ({ visible, task, onClose, onTagsUpdate, onTaskUpdated }) => {
  const {
    fetchTaskComments,
    addTaskComment,
    updateTaskComment,
    deleteTaskComment,
    fetchTaskLinks,
    addTaskLink,
    fetchTaskAttachments,
    addTaskAttachments,
    deleteTaskLink,
    deleteTaskAttachment,
    updateTask,
    loading,
  } = useTasks();
  const { getTaskAttachment } = useTasks();
  const { tags: allTags } = useTags();
  const { users: workspaceUsers, loading: usersLoading } = useWorkspaceMembers({
    enabled: visible,
  });
  const { user } = useAuth();
  const { firstName, lastName } = user || {};
  const fullName = `${firstName} ${lastName}`;

  const [isExpanded, setIsExpanded] = useState(false);
  const [showAllMeta, setShowAllMeta] = useState(false);
  const [comments, setComments] = useState([]);
  const [taskLinks, setTaskLinks] = useState([]);
  const [taskAttachments, setTaskAttachments] = useState([]);
  const [localTags, setLocalTags] = useState(task?.tags || []);
  const [localTask, setLocalTask] = useState(task || null);
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState(null);
  const [savingField, setSavingField] = useState(null);

  const { formatDateTime } = useFormattedDate();

  const { uploadFile } = useFileUpload();
  const uploadedImageMapRef = useRef({ byUrl: new Map(), byUrlBase: new Map() });

  useEffect(() => {
    setLocalTags(task?.tags || []);
    setLocalTask(task || null);
    setEditingField(null);
    setEditingValue(null);
    setSavingField(null);
    uploadedImageMapRef.current.byUrl.clear();
    uploadedImageMapRef.current.byUrlBase.clear();
  }, [task]);

  useEffect(() => {
    if (!visible) return;

    // Clear previous task data immediately
    setComments([]);
    setTaskLinks([]);
    setTaskAttachments([]);

    if (!task?.id || !task?.projectId) return;

    const loadData = async () => {
      try {
        const [commentsData, linksData, attachmentsData] = await Promise.all([
          fetchTaskComments(task.projectId, task.id),
          fetchTaskLinks(task.id),
          fetchTaskAttachments
            ? fetchTaskAttachments(task.projectId, task.id)
            : Promise.resolve([]),
        ]);
        setComments(commentsData || []);
        setTaskLinks(linksData || []);
        const normalized = (
          Array.isArray(attachmentsData) ? attachmentsData : []
        ).map((a) => {
          const url = a.fileUrl || a.url || a.file_url || a.meta?.url || null;
          let name = a.label || a.fileName || a.filename || a.name || null;
          if (!name && url) {
            try {
              const path = url.split("?")[0];
              const parts = path.split("/");
              name = decodeURIComponent(parts[parts.length - 1] || "");
            } catch (e) {
              name = null;
            }
          }

          let type = a.type || a.mimeType || null;
          if (!type && name) {
            const ext = (name.match(/\.([a-z0-9]+)$/i) || [])[1];
            if (ext) {
              if (/(jpg|jpeg|png|gif|webp|bmp|svg)/i.test(ext))
                type = `image/${ext === "jpg" ? "jpeg" : ext}`;
              else if (/(pdf)/i.test(ext)) type = "application/pdf";
            }
          }

          return {
            id: a.id,
            fileId: a.fileId || a.file_id || null,
            url,
            name,
            type,
            createdAt: a.createdAt || a.created_at || null,
            createdBy: a.createdBy || a.created_by || null,
            ...a,
          };
        });
        setTaskAttachments(normalized || []);
      } catch (err) {
        console.error("Failed to load comments/links/attachments", err);
        setComments([]);
        setTaskLinks([]);
        setTaskAttachments([]);
      }
    };

    loadData();
  }, [
    visible,
    task?.id,
    task?.projectId,
    fetchTaskAttachments,
    fetchTaskComments,
    fetchTaskLinks,
  ]);
  
  const activeTask = localTask || task;
  const activeAssignee = getTaskAssigneeInfo(activeTask || {});
  const UNKNOWN_ASSIGNEE_VALUE = "__unknown__";
  const assigneeOptions = useMemo(
    () =>
      [
        { value: UNKNOWN_ASSIGNEE_VALUE, label: "Unknown" },
        ...(Array.isArray(workspaceUsers) ? workspaceUsers : []).map((u) => ({
          value: u.id,
          label: formatNameWithEmail(
            getUserDisplayName(u) ||
              getWorkspaceMemberDisplayName(u) ||
              String(u.id),
            getWorkspaceMemberEmail(u),
          ),
        })),
      ],
    [workspaceUsers]
  );
  const mentionUsers = useMemo(
    () =>
      (Array.isArray(workspaceUsers) ? workspaceUsers : [])
        .filter((u) => u && u.id)
        .map((u) => ({
          id: String(u.id),
          name: getWorkspaceMemberDisplayName(u),
          email: getWorkspaceMemberEmail(u),
        })),
    [workspaceUsers],
  );
  const processedDescription = useProcessedDescription(
    activeTask?.description,
    activeTask?.textFiles,
  );
  const processedAcceptanceCriteria = useProcessedDescription(
    activeTask?.acceptanceCriteria,
    activeTask?.textFiles,
  );
  const editableDescriptionHtml = useMemo(
    () => convertArrayRefsToFileService(activeTask?.description || ""),
    [activeTask?.description],
  );
  const editableAcceptanceHtml = useMemo(
    () => convertArrayRefsToFileService(activeTask?.acceptanceCriteria || ""),
    [activeTask?.acceptanceCriteria],
  );
  const existingImageMap = useMemo(() => {
    const map = { byUrl: new Map(), byUrlBase: new Map() };
    const textFiles = Array.isArray(activeTask?.textFiles) ? activeTask.textFiles : [];
    const html = `${processedDescription || ""}${processedAcceptanceCriteria || ""}`;
    if (!html || textFiles.length === 0) return map;

    const imgMatches = [...html.matchAll(/<img\b[^>]*src=["']([^"']+)["'][^>]*>/gi)];
    imgMatches.forEach((match, index) => {
      const src = match[1];
      const fileRef = textFiles[index];
      if (!src || !fileRef?.fileId || src.startsWith("file-service://")) return;

      const widthMatch = match[0].match(/width=["']?(\d+)["']?/i);
      const heightMatch = match[0].match(/height=["']?(\d+)["']?/i);
      const info = {
        fileId: fileRef.fileId,
        width: fileRef.width || (widthMatch ? parseInt(widthMatch[1], 10) : 1) || 1,
        height: fileRef.height || (heightMatch ? parseInt(heightMatch[1], 10) : 1) || 1,
      };
      map.byUrl.set(src, info);
      map.byUrlBase.set(normalizeUrl(src), info);
    });

    return map;
  }, [activeTask?.textFiles, processedAcceptanceCriteria, processedDescription]);

  const fetchSignedImageUrl = useCallback(async (fileId) => {
    if (!fileId) return null;

    try {
      const response = await apiClient.get(
        `/storage-service/api/v1/files/photo/signed-urls?fids=${fileId}`,
      );
      const payload = response?.data;
      if (Array.isArray(payload) && payload.length > 0) return payload[0];
      if (Array.isArray(payload?.signedUrls) && payload.signedUrls.length > 0) {
        return payload.signedUrls[0];
      }
      if (typeof payload?.url === "string" && payload.url.trim()) return payload.url;
    } catch (error) {
      console.error("[TaskDrawer] failed to fetch signed image url", {
        fileId,
        error: error?.message,
      });
    }
    return null;
  }, []);

  const getImageDimensions = useCallback(
    (file) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
          resolve({ width: img.width, height: img.height });
          URL.revokeObjectURL(objectUrl);
        };
        img.onerror = (error) => {
          URL.revokeObjectURL(objectUrl);
          reject(error);
        };
        img.src = objectUrl;
      }),
    [],
  );

  const normalizeEditorHtmlForSave = useCallback(
    (html = "") => {
      if (!html) return html;

      const normalizedHtml = html.replace(
        /<img\b[^>]*src=["']([^"']+)["'][^>]*>/gi,
        (imgTag, src) => {
          if (src?.startsWith("file-service://")) return imgTag;

          const info =
            uploadedImageMapRef.current.byUrl.get(src) ||
            uploadedImageMapRef.current.byUrlBase.get(normalizeUrl(src)) ||
            existingImageMap.byUrl.get(src) ||
            existingImageMap.byUrlBase.get(normalizeUrl(src));
          if (!info?.fileId) return imgTag;

          const widthMatch = imgTag.match(/width=["']?(\d+)["']?/i);
          const heightMatch = imgTag.match(/height=["']?(\d+)["']?/i);
          const width = info.width || (widthMatch ? parseInt(widthMatch[1], 10) : 1) || 1;
          const height =
            info.height || (heightMatch ? parseInt(heightMatch[1], 10) : 1) || 1;
          return `[${info.fileId}, ${width}, ${height}]`;
        },
      );

      return convertFileServiceImagesToArrayRefs(normalizedHtml);
    },
    [existingImageMap],
  );

  const handleEditorImagePaste = useCallback(
    async (_file, helpers) => {
      const {
        insertPlaceholder,
        replacePlaceholderWithText,
        replacePlaceholderWithImage,
        getHtml,
      } = helpers;
      const extension = _file.type?.split("/")[1] || "png";
      const filename =
        _file.name && _file.name.trim().length > 0
          ? _file.name
          : `pasted-${Date.now()}.${extension}`;
      const placeholderText = `Uploading ${filename}... please wait`;
      const placeholderRange = insertPlaceholder(placeholderText);

      try {
        const fileForUpload = new File([_file], filename, {
          type: _file.type || "image/png",
        });
        let dimensions = { width: 1, height: 1 };
        try {
          dimensions = await getImageDimensions(fileForUpload);
        } catch (dimError) {
          console.warn("[TaskDrawer] failed to calculate image dimensions", dimError);
        }

        const uploadResult = await uploadFile(user?.id, fileForUpload, {
          purpose: "TASK_PHOTOS",
          fileType: "PHOTO",
        });
        const fileId = uploadResult?.fileId;
        if (!fileId) {
          throw new Error("missing-file-id");
        }

        const signedUrl =
          (await fetchSignedImageUrl(fileId)) ||
          uploadResult?.meta?.url ||
          uploadResult?.url ||
          null;

        if (signedUrl) {
          const info = {
            fileId,
            width: dimensions.width || 1,
            height: dimensions.height || 1,
          };
          uploadedImageMapRef.current.byUrl.set(signedUrl, info);
          uploadedImageMapRef.current.byUrlBase.set(normalizeUrl(signedUrl), info);
          replacePlaceholderWithImage(placeholderRange, signedUrl);
        } else {
          const fileServiceImg = `<img src="file-service://${fileId}" width="${dimensions.width || 1}" height="${dimensions.height || 1}" alt="image" />`;
          replacePlaceholderWithText(placeholderRange, fileServiceImg);
        }

        setEditingValue(getHtml());
      } catch (error) {
        console.error("[TaskDrawer] inline image upload failed", error);
        replacePlaceholderWithText(placeholderRange, "(image upload failed)");
        setEditingValue(getHtml());
        message.error("Failed to upload pasted image. Please try again.");
      }
    },
    [fetchSignedImageUrl, getImageDimensions, uploadFile, user?.id],
  );

  useEffect(() => {
    if (editingField !== "description") return;
    if (!processedDescription) return;
    if (editingValue !== editableDescriptionHtml) return;
    setEditingValue(processedDescription);
  }, [editingField, editingValue, editableDescriptionHtml, processedDescription]);

  useEffect(() => {
    if (editingField !== "acceptanceCriteria") return;
    if (!processedAcceptanceCriteria) return;
    if (editingValue !== editableAcceptanceHtml) return;
    setEditingValue(processedAcceptanceCriteria);
  }, [
    editingField,
    editingValue,
    editableAcceptanceHtml,
    processedAcceptanceCriteria,
  ]);

  if (!task) return null;

  const resolveAssigneeId = () => {
    const assignee = getTaskAssigneeInfo(activeTask || {});
    if (assignee.assigneeId) return assignee.assigneeId;
    if (!Array.isArray(workspaceUsers)) return null;

    const targetName = (assignee.normalizedName || "").toLowerCase();
    const targetEmail = (assignee.assigneeEmail || "").toLowerCase();

    const match = workspaceUsers.find((u) => {
      const userFullName = getWorkspaceMemberDisplayName(u).toLowerCase();
      const userEmail = getWorkspaceMemberEmail(u).toLowerCase();
      return (
        (targetEmail && userEmail === targetEmail) ||
        (targetName && userFullName === targetName)
      );
    });
    return match?.id || null;
  };

  const buildTaskPayload = (overrides = {}) => {
    const base = activeTask || {};
    const baseAssignee = getTaskAssigneeInfo(base);
    const tagIds = overrides.tagIds ?? (localTags || []).map((t) => t.id).filter(Boolean);
    return {
      type: base.type || "TASK",
      title: base.title || "",
      description: base.description || null,
      acceptanceCriteria: base.acceptanceCriteria || null,
      status: base.status || "OPEN",
      priority: base.priority ?? 2,
      points: base.points ?? 1,
      assignmentStatus: base.assignmentStatus || null,
      iterationId: base.iterationId || base.iteration?.id || null,
      startedAt: base.startedAt || null,
      expectedCompletionDate: base.expectedCompletionDate || null,
      actualCompletionDate: base.actualCompletionDate || null,
      tagIds,
      ...(baseAssignee.assigneeId ? { assigneeId: baseAssignee.assigneeId } : {}),
      ...overrides,
    };
  };

  const startEdit = (field, value) => {
    setEditingField(field);
    if (field === "description") {
      setEditingValue(processedDescription || editableDescriptionHtml);
    } else if (field === "acceptanceCriteria") {
      setEditingValue(processedAcceptanceCriteria || editableAcceptanceHtml);
    } else {
      setEditingValue(value);
    }
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditingValue(null);
  };

  const handleSaveEdit = async () => {
    if (!activeTask?.projectId || !activeTask?.id || !editingField) return;

    setSavingField(editingField);
    try {
      const overrides = {};

      switch (editingField) {
        case "title":
          overrides.title = (editingValue || "").trim();
          break;
        case "description":
          overrides.description = normalizeEditorHtmlForSave(editingValue || "");
          break;
        case "acceptanceCriteria":
          overrides.acceptanceCriteria = normalizeEditorHtmlForSave(editingValue || "");
          break;
        case "status":
          overrides.status = editingValue || "OPEN";
          break;
        case "priority":
          overrides.priority = editingValue ?? activeTask.priority;
          break;
        case "points":
          overrides.points = editingValue ?? activeTask.points;
          break;
        case "assignmentStatus":
          overrides.assignmentStatus = editingValue || null;
          break;
        case "assigneeId": {
          overrides.assigneeId =
            editingValue === UNKNOWN_ASSIGNEE_VALUE ? null : editingValue || null;
          break;
        }
        case "startedAt":
          overrides.startedAt = editingValue ? dayjs(editingValue).toISOString() : null;
          break;
        case "expectedCompletionDate":
          overrides.expectedCompletionDate = editingValue ? dayjs(editingValue).toISOString() : null;
          break;
        case "actualCompletionDate":
          overrides.actualCompletionDate = editingValue ? dayjs(editingValue).toISOString() : null;
          break;
        default:
          break;
      }

      const payload = buildTaskPayload(overrides);
      const updated = await updateTask(activeTask.projectId, activeTask.id, payload);
      setLocalTask(updated || activeTask);
      onTaskUpdated?.(updated || activeTask);
      if (editingField === "tagIds" && updated?.tags) {
        setLocalTags(updated.tags);
        onTagsUpdate?.(updated.tags);
      }
      message.success("Task updated");
      cancelEdit();
    } catch (err) {
      console.error("Failed to update task", err);
      message.error("Failed to update task");
    } finally {
      setSavingField(null);
    }
  };

  // all meta rows
  const metaItems = [
    {
      icon: <CheckCircleOutlined />,
      label: "Status",
      value: (
        <EditableField
          isEditing={editingField === "status"}
          saving={savingField === "status"}
          display={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: statusMap[activeTask.status]?.color || "#9ca3af",
                  display: "inline-block",
                }}
              />
              <Text style={{ fontWeight: 500 }}>{activeTask.status}</Text>
            </div>
          }
          editor={
            <Select
              value={editingValue}
              onChange={setEditingValue}
              options={taskStatuses}
              style={{ minWidth: 180 }}
            />
          }
          onEdit={() => startEdit("status", activeTask.status)}
          onCancel={cancelEdit}
          onSave={handleSaveEdit}
        />
      ),
    },
    {
      icon: <CalendarOutlined />,
      label: "Due date",
      value: (
        <EditableField
          isEditing={editingField === "expectedCompletionDate"}
          saving={savingField === "expectedCompletionDate"}
          display={<Text>{activeTask.expectedCompletionDate || "--"}</Text>}
          editor={
            <DatePicker
              value={editingValue}
              onChange={setEditingValue}
              style={{ minWidth: 180 }}
            />
          }
          onEdit={() =>
            startEdit(
              "expectedCompletionDate",
              activeTask.expectedCompletionDate ? dayjs(activeTask.expectedCompletionDate) : null
            )
          }
          onCancel={cancelEdit}
          onSave={handleSaveEdit}
        />
      ),
    },
    {
      icon: <UserOutlined />,
      label: "Assignee",
      value: (
        <EditableField
          isEditing={editingField === "assigneeId"}
          saving={savingField === "assigneeId"}
          display={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Avatar size={22}>
                {(activeAssignee.label !== "Unknown" ? activeAssignee.label : "--")
                  ?.split(" ")
                  .map((n) => n[0])
                  .join("")}
              </Avatar>
              <Text title={activeAssignee.assigneeEmail || "No assignee email available"}>
                {activeAssignee.label !== "Unknown" ? activeAssignee.label : "--"}
              </Text>
            </div>
          }
          editor={
            <Select
              allowClear
              loading={usersLoading}
              options={assigneeOptions}
              value={editingValue}
              onChange={setEditingValue}
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              style={{ minWidth: 220 }}
              placeholder="Select assignee"
              getPopupContainer={(trigger) => trigger.parentElement}
            />
          }
          onEdit={() => startEdit("assigneeId", resolveAssigneeId())}
          onCancel={cancelEdit}
          onSave={handleSaveEdit}
        />
      ),
    },
    {
      icon: <TagOutlined />,
      label: "Tags",
      value: (
        <SimpleTagManager
          entityType="task"
          entityId={activeTask.id}
          projectId={activeTask.projectId}
          currentTags={localTags}
          availableTags={allTags}
          onChange={(updatedTags) => {
            setLocalTags(updatedTags);
          }}
        />
      ),
    },
    {
      icon: <FileTextOutlined />,
      label: "Priority",
      value: (
        <EditableField
          isEditing={editingField === "priority"}
          saving={savingField === "priority"}
          display={<Text>{activeTask.priority}</Text>}
          editor={
            <Select
              value={editingValue}
              onChange={setEditingValue}
              options={priorities}
              style={{ minWidth: 160 }}
            />
          }
          onEdit={() => startEdit("priority", activeTask.priority)}
          onCancel={cancelEdit}
          onSave={handleSaveEdit}
        />
      ),
    },
    {
      icon: <CheckSquareOutlined />,
      label: "Points",
      value: (
        <EditableField
          isEditing={editingField === "points"}
          saving={savingField === "points"}
          display={<Text>{activeTask.points}</Text>}
          editor={
            <Select
              value={editingValue}
              onChange={setEditingValue}
              options={storyPoints}
              style={{ minWidth: 120 }}
            />
          }
          onEdit={() => startEdit("points", activeTask.points)}
          onCancel={cancelEdit}
          onSave={handleSaveEdit}
        />
      ),
    },
    {
      icon: <UserOutlined />,
      label: "Created by",
      value: <Text>{activeTask.createdByName}</Text>,
    },
    {
      icon: <CalendarOutlined />,
      label: "Started at",
      value: (
        <EditableField
          isEditing={editingField === "startedAt"}
          saving={savingField === "startedAt"}
          display={<Text>{formatDateTime(activeTask.startedAt) || "--"}</Text>}
          editor={
            <DatePicker
              value={editingValue}
              onChange={setEditingValue}
              style={{ minWidth: 180 }}
            />
          }
          onEdit={() =>
            startEdit("startedAt", activeTask.startedAt ? dayjs(activeTask.startedAt) : null)
          }
          onCancel={cancelEdit}
          onSave={handleSaveEdit}
        />
      ),
    },
    {
      icon: <CalendarOutlined />,
      label: "Expected Completion",
      value: (
        <EditableField
          isEditing={editingField === "expectedCompletionDate"}
          saving={savingField === "expectedCompletionDate"}
          display={<Text>{formatDateTime(activeTask.expectedCompletionDate) || "--"}</Text>}
          editor={
            <DatePicker
              value={editingValue}
              onChange={setEditingValue}
              style={{ minWidth: 180 }}
            />
          }
          onEdit={() =>
            startEdit(
              "expectedCompletionDate",
              activeTask.expectedCompletionDate ? dayjs(activeTask.expectedCompletionDate) : null
            )
          }
          onCancel={cancelEdit}
          onSave={handleSaveEdit}
        />
      ),
    },
    {
      icon: <CalendarOutlined />,
      label: "Actual Completion",
      value: (
        <EditableField
          isEditing={editingField === "actualCompletionDate"}
          saving={savingField === "actualCompletionDate"}
          display={<Text>{formatDateTime(activeTask.actualCompletionDate) || "--"}</Text>}
          editor={
            <DatePicker
              value={editingValue}
              onChange={setEditingValue}
              style={{ minWidth: 180 }}
            />
          }
          onEdit={() =>
            startEdit(
              "actualCompletionDate",
              activeTask.actualCompletionDate ? dayjs(activeTask.actualCompletionDate) : null
            )
          }
          onCancel={cancelEdit}
          onSave={handleSaveEdit}
        />
      ),
    },
    activeTask.iteration
      ? {
          icon: <CalendarOutlined />,
          label: "Iteration",
          value: <Text>{activeTask.iteration.name}</Text>,
        }
      : null,
    activeTask.assignmentStatus
      ? {
          icon: <CheckCircleOutlined />,
          label: "Assignment status",
          value: (
            <EditableField
              isEditing={editingField === "assignmentStatus"}
              saving={savingField === "assignmentStatus"}
              display={<Text>{activeTask.assignmentStatus}</Text>}
              editor={
                <Select
                  value={editingValue}
                  onChange={setEditingValue}
                  options={assignmentStatuses}
                  allowClear
                  style={{ minWidth: 200 }}
                />
              }
              onEdit={() => startEdit("assignmentStatus", activeTask.assignmentStatus)}
              onCancel={cancelEdit}
              onSave={handleSaveEdit}
            />
          ),
        }
      : null,
  ].filter(Boolean);

  // number of items to show initially
  const visibleCount = 6;

  const handleAddComment = async (content) => {
    const tempId = `temp-${Date.now()}`;

    const optimisticComment = {
      id: tempId,
      createdByName: fullName,
      avatar: "YO",
      content,
      createdAt: dayjs().toISOString(),
    };

    setComments((prev) => [optimisticComment, ...prev]);

    try {
      const saved = await addTaskComment(task.projectId, task.id, { content });
      setComments((prev) => prev.map((c) => (c.id === tempId ? saved : c)));
    } catch (err) {
      // rollback
      setComments((prev) => prev.filter((c) => c.id !== tempId));
    }
  };

  const handleEditComment = async (commentId, content) => {
    try {
      const updated = await updateTaskComment(
        task.projectId,
        task.id,
        commentId,
        { content },
      );

      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? updated : c)),
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteTaskComment(task.projectId, task.id, commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddLink = async (values) => {
    if (!values.label || !values.url) return;

    const newLink = {
      label: values.label,
      url: values.url,
    };

    // optimistic update
    const tempId = `temp-${Date.now()}`;
    setTaskLinks((prev) => [...prev, { ...newLink, id: tempId }]);

    try {
      const saved = await addTaskLink(task.id, newLink);
      setTaskLinks((prev) => prev.map((l) => (l.id === tempId ? saved[0] : l)));
    } catch (err) {
      console.error("Failed to add link", err);
      // rollback
      setTaskLinks((prev) => prev.filter((l) => l.id !== tempId));
    }
  };

  const handleAddFile = (fileData) => {
    setTaskAttachments((prev) => {
      const newAttachments = [...prev, fileData];
      return newAttachments;
    });
  };

  const isImageFile = (file) => {
    if (!file) return false;
    if (typeof file === "string") {
      const lower = file.toLowerCase();
      return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(lower);
    }
    const type = String(file.type || "").toLowerCase();
    if (type) return type.startsWith("image/");
    return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(String(file.name || ""));
  };

  const handleFileUpload = async (file) => {
    try {
      message.loading({ content: `Uploading ${file.name}...`, key: "upload" });
      const fileType = isImageFile(file) ? "PHOTO" : "DOCUMENT";
      const uploadResult = await uploadFile(user?.id, file, {
        purpose: "USER_PROFILE",
        fileType,
      });

      const attachmentData = [
        {
          fileId: uploadResult.fileId,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          url: uploadResult.meta?.url,
        },
      ];

      const saved = await addTaskAttachments(
        task.projectId,
        task.id,
        attachmentData,
      );
      const savedAttachments = Array.isArray(saved) ? saved : [saved];

      message.success({ content: `${file.name} uploaded`, key: "upload" });
      return savedAttachments[0];
    } catch (err) {
      console.error("Failed to upload file from TaskDrawer", err);
      message.error({
        content: `Failed to upload ${file.name}`,
        key: "upload",
      });
      throw err;
    }
  };

  const handleDeleteAttachment = async (id, type) => {
    try {
      if (type === "link") {
        await deleteTaskLink(task.id, id);
        setTaskLinks((prev) => prev.filter((l) => l.id !== id));
        message.success("Link deleted");
      } else if (type === "file") {
        await deleteTaskAttachment(task.projectId, task.id, id);
        setTaskAttachments((prev) => prev.filter((a) => a.id !== id));
        message.success("Attachment deleted");
      }
    } catch (err) {
      console.error("Failed to delete attachment", err);
      message.error("Failed to delete");
    }
  };

  const shareUrl =
    window.location.origin +
    paths.projectTaskDetails(task.projectId, task.iterationId, task.id);

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => message.success("Link copied to clipboard"))
      .catch(() => message.error("Failed to copy link"));
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(`Task: ${task.title}`);
    const body = encodeURIComponent(
      `Hi,\n\nCheck out this task: ${shareUrl}\n\nBest regards`,
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const items = [
    {
      key: "copy",
      icon: <LinkOutlined />,
      label: "Copy Link",
      onClick: handleCopyLink,
    },
    {
      key: "email",
      icon: <MailOutlined />,
      label: "Share via Email",
      onClick: handleEmailShare,
    },
  ];

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      placement="right"
      size={isExpanded ? 520 : "100vw"}
      zIndex={99999}
      styles={{ padding: 24 }}
      title={null}
      closable={false}
      destroyOnHidden
    >
      <div
        style={{
          maxWidth: isExpanded ? "100vw" : 700,
          margin: isExpanded ? 0 : "0 auto",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
            <div style={{ fontSize: 13, color: "#6b7280" }}>
            <span style={{ cursor: "pointer" }}>Project</span>
            <span style={{ margin: "0 6px" }}>/</span>
            <span style={{ color: "#111827", fontWeight: 500 }}>
              On Progress
            </span>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            <ExpandOutlined
              className="task-drawer-icon"
              onClick={() => setIsExpanded((prev) => !prev)}
              style={{ cursor: "pointer" }}
            />
            <Dropdown
              menu={{ items }}
              placement="bottomRight"
              trigger={["click"]}
            >
              <ShareAltOutlined
                className="task-drawer-icon"
                style={{ cursor: "pointer" }}
              />
            </Dropdown>

            <CloseCircleOutlined
              className="task-drawer-icon"
              onClick={onClose}
            />
          </div>
        </div>

        {/* Title */}
        <Title level={4} style={{ marginBottom: 16 }}>
          <EditableField
            isEditing={editingField === "title"}
            saving={savingField === "title"}
            display={<span>{activeTask.title}</span>}
            editor={
              <Input
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                style={{ minWidth: 260 }}
              />
            }
            onEdit={() => startEdit("title", activeTask.title)}
            onCancel={cancelEdit}
            onSave={handleSaveEdit}
          />
        </Title>

        {/* <EmailShareButton
          url={shareUrl}
          subject={`Task: ${task.title}`}
          body={`Hi,\n\nPlease check this task:\n${shareUrl}`}
        >
          <span>Share via Email</span>
        </EmailShareButton>

        <Button type="link" icon={<MailOutlined />} onClick={handleEmailShare}>
          Share via Email
        </Button> */}

        {/* Status / Meta section */}
        <div style={{ marginBottom: 20 }}>
          {(showAllMeta ? metaItems : metaItems.slice(0, visibleCount)).map(
            (item, index) => (
              <MetaRow
                key={index}
                icon={item.icon}
                label={item.label}
                value={item.value}
              />
            ),
          )}

          {metaItems.length > visibleCount && (
            <Text
              onClick={() => setShowAllMeta((prev) => !prev)}
              style={{
                cursor: "pointer",
                color: "#3b82f6",
                // color: "#00d27a",
                fontWeight: 500,
                fontFamily: "Inter",
                fontSize: 13,
                userSelect: "none",
              }}
            >
              {showAllMeta
                ? "Show less"
                : `Show more (${metaItems.length - visibleCount})`}
            </Text>
          )}
        </div>

        {/* Description block */}
        <div style={{ marginBottom: 24 }}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#6b7280",
              fontSize: 13,
              marginBottom: 8,
              fontFamily: "Inter",
            }}
          >
            <FileTextOutlined />
            <span>Description</span>
          </div>

          {/* Content */}
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 4,
              padding: 12,
              background: "#fafafa",
            }}
            onDoubleClick={() =>
              editingField === null &&
              startEdit("description", activeTask.description || "")
            }
          >
            {editingField === "description" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <MentionEditor
                  value={editingValue || ""}
                  onChange={setEditingValue}
                  users={mentionUsers}
                  onImagePaste={handleEditorImagePaste}
                  placeholder="Describe the task..."
                  height="220px"
                  debugId="TaskDrawer:Description"
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <CheckOutlined
                    onClick={handleSaveEdit}
                    style={{ cursor: savingField === "description" ? "not-allowed" : "pointer" }}
                  />
                  <CloseOutlined onClick={cancelEdit} style={{ cursor: "pointer" }} />
                </div>
              </div>
            ) : (
              <div
                style={{ color: "#374151", fontSize: 13, margin: 0, padding: 0 }}
                dangerouslySetInnerHTML={{
                  __html: processedDescription || "<p>No description provided</p>",
                }}
              />
            )}
          </div>
        </div>

        {/* Acceptance Criteria */}
        {activeTask.acceptanceCriteria && (
          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "#6b7280",
                fontSize: 13,
                marginBottom: 8,
              }}
              >
                <IssuesCloseOutlined />
                <span>Acceptance Criteria</span>
            </div>
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 4,
                padding: 12,
                background: "#fafafa",
              }}
              onDoubleClick={() =>
                editingField === null &&
                startEdit("acceptanceCriteria", activeTask.acceptanceCriteria || "")
              }
            >
              {editingField === "acceptanceCriteria" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <MentionEditor
                    value={editingValue || ""}
                    onChange={setEditingValue}
                    users={mentionUsers}
                    onImagePaste={handleEditorImagePaste}
                    placeholder="Detail the acceptance criteria..."
                    height="220px"
                    debugId="TaskDrawer:AcceptanceCriteria"
                  />
                  <div style={{ display: "flex", gap: 8 }}>
                    <CheckOutlined
                      onClick={handleSaveEdit}
                      style={{ cursor: savingField === "acceptanceCriteria" ? "not-allowed" : "pointer" }}
                    />
                    <CloseOutlined onClick={cancelEdit} style={{ cursor: "pointer" }} />
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    color: "#374151",
                    fontSize: 13,
                    margin: 0,
                    padding: 0,
                  }}
                  dangerouslySetInnerHTML={{
                    __html:
                      processedAcceptanceCriteria ||
                      "<p>No acceptance criteria provided</p>",
                  }}
                />
              )}
            </div>
          </div>
        )}
        <AttachmentsSection
          links={taskLinks || []}
          files={taskAttachments || []}
          onAddLink={handleAddLink}
          onAddFile={handleAddFile}
          onFileUpload={handleFileUpload}
          onDelete={handleDeleteAttachment}
          onGetAttachment={
            getTaskAttachment
              ? (attachmentId) => getTaskAttachment(activeTask.projectId, activeTask.id, attachmentId)
              : undefined
          }
        />

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: "#6b7280",
            fontSize: 13,
            marginBottom: 8,
            fontFamily: "Inter",
          }}
        >
          <WechatWorkOutlined />
          <span>Comments ({comments.length})</span>
        </div>
        <CommentsSection
          comments={comments}
          onAdd={handleAddComment}
          onEdit={handleEditComment}
          onDelete={handleDeleteComment}
          loading={loading}
          mentionUsers={mentionUsers}
          user={user}
        />
      </div>
    </Drawer>
  );
};

/* Helper Components */
const MetaRow = ({ icon, label, value }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      marginBottom: 14,
    }}
  >
    {/* Left label */}
    <div
      style={{
        // width: 160,
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: "#6b7280",
        fontSize: 13,
        flex: 1,
      }}
    >
      {icon}
      <span>{label}</span>
    </div>

    {/* Right value */}
    <div style={{ flex: 2 }}>{value}</div>
  </div>
);

const EditableField = ({ isEditing, saving, display, editor, onEdit, onSave, onCancel }) => {
  if (isEditing) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {editor}
        <CheckOutlined
          onClick={saving ? undefined : onSave}
          style={{
            cursor: saving ? "not-allowed" : "pointer",
            color: saving ? "#9ca3af" : "#22c55e",
          }}
        />
        <CloseOutlined
          onClick={saving ? undefined : onCancel}
          style={{ cursor: saving ? "not-allowed" : "pointer", color: "#ef4444" }}
        />
      </div>
    );
  }

  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
      onDoubleClick={onEdit}
    >
      {display}
    </div>
  );
};

export default TaskDrawer;
